import { Form, Button, Row, Col, Table, Alert } from 'react-bootstrap';
import { connect } from 'react-redux';
import axiosInstance from '../general/axiosInstance';
import React, { useEffect, useState, useMemo } from 'react';
import { setIsLoading } from '../general/actions';
import { currencyFormater, downloadExcel } from '../general/utils';
import SelectAgreement from '../general/selectAgreement';
import { C } from '../general/colors';

function SortBtn({ col, current, onSort, label }) {
    const active = current === col;
    return (
        <button onClick={() => onSort(col)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: active ? 800 : 600, color: active ? 'var(--abi-primary)' : 'inherit', whiteSpace: 'nowrap' }}>
            {label}
            {active && <i className="fa fa-sort-desc" style={{ marginLeft: 4, fontSize: 10 }} />}
        </button>
    );
}

function ProductsReport({ setIsLoading }) {
    const [orders, setOrders] = useState([]);
    const [agreementFilter, setAgreementFilter] = useState('');
    const [createdAtStart, setCreatedAtStart] = useState('');
    const [createdAtEnd, setCreatedAtEnd] = useState('');
    const [sortCol, setSortCol] = useState('total');
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');

    useEffect(() => {
        const today = new Date();
        const past  = new Date();
        past.setMonth(today.getMonth() - 3);
        setCreatedAtEnd(today.toISOString().slice(0, 10));
        setCreatedAtStart(past.toISOString().slice(0, 10));
    }, []);

    useEffect(() => {
        if (agreementFilter) getOrders();
        else { setOrders([]); setMessageType('info'); setMessage('Selecciona un contrato para ver el reporte'); }
    }, [agreementFilter]);

    const getOrders = async () => {
        setIsLoading(true);
        try {
            const r = await axiosInstance.get('/orders/filters/', {
                params: { agreement_id: agreementFilter, created_start_date: createdAtStart, created_end_date: createdAtEnd },
            });
            setOrders(r.data || []);
            if (!r.data.length) { setMessageType('info'); setMessage('No hay pedidos en el período seleccionado'); }
            else setMessage('');
        } catch (e) { console.log(e); }
        finally { setIsLoading(false); }
    };

    // Agrupación por producto desde los detalles de pedidos
    const products = useMemo(() => {
        const map = {};
        orders.forEach(o => {
            (o.order_detail || []).forEach(d => {
                const key = d.product.id;
                if (!map[key]) {
                    map[key] = {
                        id:         d.product.id,
                        ext_id:     d.product.ext_id,
                        name:       d.product.name,
                        type:       d.product.product_type?.name ?? '—',
                        orderCount: 0,
                        quantity:   0,
                        total:      0,
                    };
                }
                map[key].orderCount++;
                map[key].quantity += parseInt(d.quantity);
                map[key].total    += parseFloat(d.total_amount);
            });
        });
        return Object.values(map);
    }, [orders]);

    const sorted = [...products].sort((a, b) => {
        if (sortCol === 'total')   return b.total      - a.total;
        if (sortCol === 'qty')     return b.quantity   - a.quantity;
        if (sortCol === 'orders')  return b.orderCount - a.orderCount;
        return 0;
    });

    const grandTotal = sorted.reduce((s, p) => s + p.total, 0);
    const maxTotal   = sorted.length ? sorted[0].total : 1;

    return (
        <>
            {/* ── Ayuda ── */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, background: 'rgba(37,99,235,0.05)', border: '1px solid rgba(37,99,235,0.15)', borderRadius: 12, padding: '12px 16px', marginBottom: 14 }}>
                <i className="fa fa-lightbulb-o" style={{ color: 'var(--abi-primary)', fontSize: 18, marginTop: 2, flexShrink: 0 }} />
                <div style={{ fontSize: 13, color: 'var(--abi-text)', lineHeight: 1.65 }}>
                    <strong>Análisis de Productos:</strong> consolida todos los pedidos del período y muestra qué productos se han pedido más,
                    su cantidad total y su peso en el gasto total del contrato.
                    Útil para planear aprovisionamiento e identificar los ítems de mayor impacto presupuestal.
                    Haz clic en los encabezados <em>Pedidos</em>, <em>Cantidad</em> o <em>Total</em> para reordenar la tabla.
                </div>
            </div>

            {/* ── Filtros ── */}
            <div className="order-section">
                <div className="order-section-header">
                    <i className="fa fa-cubes" /> Análisis de Productos
                </div>
                <div className="order-section-body">
                    <Row className="g-3 align-items-end">
                        <Col md={4} xs={12}>
                            <Form.Label className="report-field-label">Contrato</Form.Label>
                            <SelectAgreement agreement={agreementFilter} setAgreement={setAgreementFilter} disabled={false} />
                        </Col>
                        <Col md={5} xs={12}>
                            <Form.Label className="report-field-label">
                                <i className="fa fa-calendar-plus-o" style={{ marginRight: 4 }} /> Fecha de creación
                            </Form.Label>
                            <div className="report-date-range">
                                <Form.Control type="date" value={createdAtStart} disabled={!agreementFilter} onChange={e => setCreatedAtStart(e.target.value)} />
                                <span className="report-date-sep">→</span>
                                <Form.Control type="date" value={createdAtEnd} disabled={!agreementFilter} onChange={e => setCreatedAtEnd(e.target.value)} />
                            </div>
                        </Col>
                        <Col md={3} xs={12} className="d-flex gap-2">
                            <Button variant="primary" onClick={getOrders} disabled={!agreementFilter} style={{ flex: 1 }}>
                                <i className="fa fa-filter" /> Filtrar
                            </Button>
                        </Col>
                    </Row>
                </div>
            </div>

            {message && <Alert variant={messageType} className="mt-2">{message}</Alert>}

            {sorted.length > 0 && (
                <>
                    <div className="report-toolbar">
                        <span className="report-count">
                            <strong>{sorted.length}</strong> producto{sorted.length !== 1 ? 's' : ''} en{' '}
                            <strong>{orders.length}</strong> pedido{orders.length !== 1 ? 's' : ''} — total:{' '}
                            <strong>{currencyFormater(grandTotal)}</strong>
                        </span>
                        <Button variant="outline-success" size="sm" onClick={() => {
                            const headers = ['Pos.', 'ID', 'Ext ID', 'Producto', 'Tipo', '# Pedidos', 'Cantidad', 'Total', '% Participación'];
                            const rows = sorted.map((p, i) => [
                                i + 1, p.id, p.ext_id, p.name, p.type,
                                p.orderCount, p.quantity,
                                parseFloat(p.total.toFixed(2)),
                                parseFloat(((p.total / grandTotal) * 100).toFixed(2)),
                            ]);
                            const colFormats = [null, null, null, null, null, null, null, 'currency', 'percent'];
                            downloadExcel(headers, rows, 'Análisis de Productos', 'Productos', colFormats);
                        }}>
                            <i className="fa fa-file-excel-o" /> Excel
                        </Button>
                    </div>

                    <Table responsive className="report-table">
                        <thead>
                            <tr>
                                <th style={{ width: 40 }}>#</th>
                                <th>Producto</th>
                                <th className="d-none d-md-table-cell">Tipo</th>
                                <th className="text-center">
                                    <SortBtn col="orders" current={sortCol} onSort={setSortCol} label="Pedidos" />
                                </th>
                                <th className="text-center">
                                    <SortBtn col="qty" current={sortCol} onSort={setSortCol} label="Cantidad" />
                                </th>
                                <th className="text-end">
                                    <SortBtn col="total" current={sortCol} onSort={setSortCol} label="Total" />
                                </th>
                                <th className="d-none d-lg-table-cell">Participación</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((p, i) => {
                                const pct      = grandTotal > 0 ? (p.total / grandTotal) * 100 : 0;
                                const barPct   = (p.total / maxTotal) * 100;
                                const barColor = pct >= 30 ? C.danger : pct >= 15 ? C.warning : C.success;
                                return (
                                    <tr key={'pr-' + p.id}>
                                        <td style={{ color: 'var(--abi-muted)', fontSize: 12, fontWeight: 700 }}>{i + 1}</td>
                                        <td>
                                            <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                                            <div style={{ fontSize: 11, color: 'var(--abi-muted)' }}>{p.ext_id}</div>
                                        </td>
                                        <td className="d-none d-md-table-cell">
                                            <span className="order-detail-type-badge">{p.type}</span>
                                        </td>
                                        <td className="text-center" style={{ fontWeight: 600 }}>{p.orderCount}</td>
                                        <td className="text-center" style={{ fontWeight: 600 }}>{p.quantity}</td>
                                        <td className="text-end" style={{ fontWeight: 700 }}>{currencyFormater(p.total)}</td>
                                        <td className="d-none d-lg-table-cell" style={{ minWidth: 160 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div className="report-fund-bar-wrap" style={{ flex: 1 }}>
                                                    <div className="report-fund-bar-fill" style={{ width: `${barPct}%`, background: barColor }} />
                                                </div>
                                                <span style={{ fontSize: 11, fontWeight: 700, color: barColor, minWidth: 38, textAlign: 'right' }}>
                                                    {pct.toFixed(1)}%
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="report-tfoot">
                                <th colSpan={5} className="text-end">Total</th>
                                <th className="text-end" style={{ fontSize: 15 }}>{currencyFormater(grandTotal)}</th>
                                <th className="d-none d-lg-table-cell"></th>
                            </tr>
                        </tfoot>
                    </Table>
                </>
            )}
        </>
    );
}

function mapStateToProps(state) { return { isLoading: state.isLoading }; }
const mapDispatchToProps = { setIsLoading };
export default connect(mapStateToProps, mapDispatchToProps)(ProductsReport);
